import { useSyncExternalStore } from 'react';

import { reportThrottled } from '#shared';

import { getAuthToken } from '#shared/api';
import type { AuthTokenGetter } from '#shared/api';

import { fetchPlaybackPack } from '../api';

import { mergePack, PACK_WINDOW_MS, packFromWire } from './packModel';

import type { PlaybackPack } from './packModel';

export type PlaybackPackStatus = 'idle' | 'loading' | 'ready' | 'failed';

export interface PlaybackPackState {
  status: PlaybackPackStatus;
  pack: PlaybackPack | null;
  receivedBytes: number;
}

export interface PackLoadOptions {
  getToken?: AuthTokenGetter;
  nowMs?: number;
}

// duplicate-ok: entities cannot import the same literal kept in domains
/** The BE refuses a `from` outside retention; one minute inside it, as the snapshot floor does. */
const FROM_MARGIN_MS = 60_000;
// duplicate-ok: entities cannot import the same literal kept in domains
/** A failed tail is not retried sooner, so a playhead parked at the edge cannot fetch per frame. */
const TAIL_RETRY_MS = 5_000;
/** The newest minute is drawn live, so a shorter tail buys nothing. */
export const TAIL_MIN_SPAN_MS = 60_000;
/** The tail is requested at one minute behind now and has the second minute to land. */
export const TAIL_GRACE_MS = 2 * TAIL_MIN_SPAN_MS;
// duplicate-ok: entities cannot import the same literal kept in processes and domains
const REPORT_THROTTLE_MS = 60_000;

const IDLE: PlaybackPackState = {
  status: 'idle',
  pack: null,
  receivedBytes: 0,
};

let state = IDLE;
let tailInFlight = false;
let tailBlockedUntilMs = 0;
const listeners = new Set<() => void>();

const publish = (next: PlaybackPackState): void => {
  state = next;
  listeners.forEach(listener => listener());
};

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export const getPlaybackPackState = (): PlaybackPackState => state;

// duplicate-ok: the similar event bus lives in processes, which entities cannot import
export const subscribePlaybackPack = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const usePlaybackPackState = (): PlaybackPackState =>
  useSyncExternalStore(subscribePlaybackPack, getPlaybackPackState);

const requireToken = async (getToken: AuthTokenGetter): Promise<string> => {
  const token = await getToken();
  if (!token) {
    throw new Error('map playback pack: no auth token');
  }
  return token;
};

export const loadPlaybackPack = async ({
  getToken = getAuthToken,
  nowMs = Date.now(),
}: PackLoadOptions = {}): Promise<void> => {
  if (state.status === 'loading' || state.status === 'ready') {
    return;
  }
  publish({ status: 'loading', pack: null, receivedBytes: 0 });
  try {
    const wire = await fetchPlaybackPack({
      fromIso: new Date(nowMs - PACK_WINDOW_MS + FROM_MARGIN_MS).toISOString(),
      toIso: new Date(nowMs).toISOString(),
      token: await requireToken(getToken),
      onBytes: receivedBytes => publish({ ...state, receivedBytes }),
    });
    publish({
      status: 'ready',
      pack: mergePack(null, packFromWire(wire), nowMs),
      receivedBytes: state.receivedBytes,
    });
  } catch (error) {
    reportThrottled(
      'map-playback:pack-load-failed',
      'map-playback: pack load failed',
      {
        throttleMs: REPORT_THROTTLE_MS,
        level: 'warning',
        extra: { error: errorMessage(error) },
      },
    );
    publish({ status: 'failed', pack: null, receivedBytes: 0 });
  }
};

export const extendPlaybackPack = async ({
  getToken = getAuthToken,
  nowMs = Date.now(),
}: PackLoadOptions = {}): Promise<void> => {
  const { pack } = state;
  if (
    state.status !== 'ready' ||
    pack === null ||
    tailInFlight ||
    nowMs < tailBlockedUntilMs ||
    nowMs - pack.toMs < TAIL_MIN_SPAN_MS
  ) {
    return;
  }
  tailInFlight = true;
  try {
    const wire = await fetchPlaybackPack({
      fromIso: new Date(pack.toMs).toISOString(),
      toIso: new Date(nowMs).toISOString(),
      token: await requireToken(getToken),
    });
    const current = state.pack ?? pack;
    publish({ ...state, pack: mergePack(current, packFromWire(wire), nowMs) });
  } catch (error) {
    tailBlockedUntilMs = nowMs + TAIL_RETRY_MS;
    reportThrottled(
      'map-playback:pack-tail-failed',
      'map-playback: pack tail fetch failed',
      {
        throttleMs: REPORT_THROTTLE_MS,
        level: 'warning',
        extra: { error: errorMessage(error) },
      },
    );
  } finally {
    tailInFlight = false;
  }
};

export const __resetPlaybackPack = (): void => {
  state = IDLE;
  tailInFlight = false;
  tailBlockedUntilMs = 0;
  listeners.clear();
};
